import { motion } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";

const PHOTO =
  "https://customer-assets.emergentagent.com/job_d1223cd2-eba7-448a-9e29-ea815523eca6/artifacts/bwxw0bso_WhatsApp%20Image%202026-05-06%20at%207.44.52%20PM.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export default function HeroAbout() {
  const goContact = () => {
    document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="sobre-mi"
      data-testid="hero-about-section"
      className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          className="flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-[#475569] mb-8"
        >
          <span className="inline-block w-10 h-px bg-[#0A192F]" />
          <Sparkles size={14} strokeWidth={1.5} />
          <span>Marketing Digital · E-commerce</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT: Headline + intro */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.h1
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A192F]"
            >
              Hola, soy{" "}
              <span className="italic font-medium relative">
                Melina
                <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-[#0A192F]" />
              </span>
              .
              <br />
              Ayudo a tu negocio
              <br />
              a <span className="italic">despegar</span>.
            </motion.h1>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
              className="mt-10 max-w-xl space-y-5 text-[#475569] text-base sm:text-lg leading-relaxed"
            >
              <p>
                Tengo 26 años y estoy cursando el último cuatrimestre de la
                carrera de Marketing Digital y E-commerce. Después de trabajar
                en varias empresas en puestos de marketing, me di cuenta de que
                lo que más me apasiona es{" "}
                <span className="text-[#0A192F] font-medium">
                  ayudar a personas a hacer crecer sus negocios y cumplir sus
                  sueños
                </span>
                .
              </p>
              <p>
                Por eso armé este espacio: para llegar a quienes quieren
                independizarse y darle ese empujón estratégico que su proyecto
                necesita.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={goContact}
                data-testid="hero-cta-contact"
                className="btn-lift inline-flex items-center gap-3 bg-[#0A192F] text-white px-7 py-4 text-xs uppercase tracking-[0.25em]"
              >
                Empecemos hoy
                <ArrowDownRight size={16} strokeWidth={1.5} />
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById("servicios")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                data-testid="hero-cta-services"
                className="link-underline text-xs uppercase tracking-[0.25em] text-[#0A192F] py-4"
              >
                Ver servicios
              </button>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={3}
              className="mt-14 grid grid-cols-3 gap-6 max-w-md border-t border-[#E2E8F0] pt-8"
            >
              {[
                { k: "+5", v: "Servicios" },
                { k: "100%", v: "Personalizado" },
                { k: "1:1", v: "Acompañamiento" },
              ].map((s) => (
                <div key={s.v} data-testid={`hero-stat-${s.v.toLowerCase()}`}>
                  <div className="font-display text-3xl text-[#0A192F]">
                    {s.k}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#475569]">
                    {s.v}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT: Photo */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
              className="relative"
            >
              <div className="photo-frame">
                <div className="relative overflow-hidden">
                  <img
                    src={PHOTO}
                    alt="Melina, especialista en Marketing Digital y E-commerce"
                    data-testid="hero-photo"
                    className="w-full h-[460px] sm:h-[560px] lg:h-[640px] object-cover object-center grayscale-0"
                  />
                  <div className="absolute top-4 left-4 bg-[#0A192F] text-white text-[10px] uppercase tracking-[0.3em] px-3 py-1.5">
                    est. 2026
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between text-xs uppercase tracking-[0.25em] text-[#475569]">
                <span>Buenos Aires, AR</span>
                <span className="font-display italic text-[#0A192F]">
                  Melina Marketing
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
